/* Copyright (c) 2020-present CloudIO Inc */

import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';
import { itemWithDefaultValues } from 'registry/Components';
import { Item, RequiredItem } from '../../registry/Component.type';
import { isEmpty } from 'utils/isEmpty';
import { pageAtom } from 'designer/atoms';

export async function analyzeLOVField({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const item = itemWithDefaultValues(page[itemId]) as RequiredItem<'LOVField'>;
  const { returnValueAttribute, comboDatasourceAlias } = item.props;

  const lovGridItems = platform.getChildrenOfType(
    appUid,
    pageId,
    page,
    item,
    'LOVGrid',
  );
  if (!lovGridItems.length) {
    alerts.push({
      key: `incomplete-${itemId}`,
      showItemId: itemId,
      alert: `Incomplete ${mgr.name}. Must have a LOV Grid!`,
      itemId,
    });
    return [alerts, autoFixes];
  }
  const lovGrid = lovGridItems[0];
  const cols = platform.getChildrenItems(
    appUid,
    pageId,
    page,
    lovGrid,
    (child) => {
      const c = child as Item<'StringColumn' | 'NumberColumn'>;
      return (
        c.props.viewAttribute === returnValueAttribute &&
        c.props.visible !== false &&
        isEmpty(c.props.visibleCondition)
      );
    },
  );

  if (!cols.length) {
    alerts.push({
      key: `incomplete-${itemId}`,
      showItemId: lovGrid.itemId,
      alert: `Incomplete LOV Grid. Must have an always visible column with view attribute same as the return value attribute ${returnValueAttribute}!`,
      itemId,
    });
    return [alerts, autoFixes];
  }

  const itemIds = Object.keys(page);

  const dsItem = itemIds
    .filter(
      (id) =>
        page[id]?.type === 'DataStore' ||
        page[id]?.type === 'ReadOnlyDataStore' ||
        page[id]?.type === 'LocalDataStore' ||
        page[id]?.type === 'TransientDataStore',
    )
    .map((id) => page[id] as Item<'ReadOnlyDataStore'>)
    .find((ds) => ds.props.alias === comboDatasourceAlias);

  if (!dsItem) {
    alerts.push({
      key: `missing-alias-${itemId}`,
      showItemId: itemId,
      alert: `Missing data store for alias ${comboDatasourceAlias}!`,
      itemId,
    });
    return [alerts, autoFixes];
  }

  if (dsItem.props.autoQuery) {
    const fix = () => {
      const _page = { ...platform.getPage(appUid, pageId) };
      const udpatedDs: Item<'ReadOnlyDataStore'> = {
        ...dsItem,
        props: { ...dsItem.props, autoQuery: false },
      };
      _page[dsItem.itemId] = udpatedDs;
      platform.setter(pageAtom, _page, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `auto-${itemId}`,
      fix,
      showItemId: dsItem.itemId,
      alert: `Data Store [${comboDatasourceAlias}] used by a LOV has auto query on!`,
      tip: `LOV component controls the data store query, so turn off the auto query.

 > Click \`Fix It\` to turn off the auto query on data store with alias ${comboDatasourceAlias}.`,
      itemId,
    });
  }

  return [alerts, autoFixes];
}
